class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onResultsClick.bind(this));
  }

  /* ── Letzte Suchen (localStorage, max. 5) ─────────────────────── */
  getRecentSearches() {
    try {
      return JSON.parse(localStorage.getItem('airtec-recent-searches')) || [];
    } catch (e) {
      return [];
    }
  }

  saveRecentSearch(term) {
    term = (term || '').trim();
    if (term.length < 2) return;
    try {
      const list = this.getRecentSearches().filter((t) => t.toLowerCase() !== term.toLowerCase());
      list.unshift(term);
      localStorage.setItem('airtec-recent-searches', JSON.stringify(list.slice(0, 5)));
    } catch (e) {
      /* Storage nicht verfügbar — kein Problem */
    }
  }

  showRecentSearches() {
    const list = this.getRecentSearches();
    if (!list.length) return;
    const isFr = window.location.pathname.indexOf('/fr') === 0;
    const esc = (s) => {
      const d = document.createElement('div');
      d.textContent = s;
      return d.innerHTML.replace(/"/g, '&quot;');
    };
    const items = list
      .map(
        (t, i) =>
          `<li id="predictive-search-option-recent-${i + 1}" class="predictive-search__list-item airtec-ps__recent-item" role="option" aria-selected="false">` +
          `<button type="button" class="airtec-ps__recent-btn" data-term="${esc(t)}" tabindex="-1">${esc(t)}</button></li>`
      )
      .join('');
    this.predictiveSearchResults.innerHTML =
      `<div id="predictive-search-results" role="listbox"><div class="airtec-ps__recent">` +
      `<p class="airtec-ps__section-label">${isFr ? 'Recherches récentes' : 'Letzte Suchen'}` +
      `<button type="button" class="airtec-ps__recent-clear" tabindex="-1">${isFr ? 'effacer' : 'löschen'}</button></p>` +
      `<ul class="list-unstyled" role="group">${items}</ul></div></div>`;
    this.setAttribute('results', true);
    this.open();
  }

  onResultsClick(event) {
    if (!event.target.closest) return;
    const recentBtn = event.target.closest('.airtec-ps__recent-btn');
    if (recentBtn) {
      event.preventDefault();
      this.input.value = recentBtn.getAttribute('data-term');
      this.input.focus();
      this.onChange();
      return;
    }
    if (event.target.closest('.airtec-ps__recent-clear')) {
      try {
        localStorage.removeItem('airtec-recent-searches');
      } catch (e) {}
      this.close(true);
      return;
    }
    // Klick auf einen Treffer: Suchbegriff für «Letzte Suchen» merken
    if (event.target.closest('.airtec-ps__product-link, .airtec-ps__category-link, .airtec-ps__suggestion')) {
      this.saveRecentSearch(this.getQuery());
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      this.showRecentSearches();
      return;
    }

    // Debouncing: Warte 300ms nach dem letzten Tastendruck
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.getSearchResults(this.searchTerm);
    }, 300);
  }

  onFormSubmit(event) {
    // Nachgestellte Punkte/Striche entfernen, damit die Suchseite Treffer findet
    this.input.value = this.input.value.replace(/[.\-\s]+$/, '');
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    this.saveRecentSearch(this.getQuery());
    // Exakte Bestell-Nr. eingegeben → direkt zur Produktseite (passende Variante) statt Suchseite
    const exactLink = this.findExactSkuLink();
    if (exactLink) {
      event.preventDefault();
      window.location.href = exactLink.href;
    }
  }

  findExactSkuLink() {
    const term = this.getQuery().trim().toLowerCase();
    if (!term) return null;
    let link = null;
    this.querySelectorAll('.airtec-ps__product-link').forEach((a) => {
      if (link) return;
      const skuEl = a.querySelector('.airtec-ps__product-sku[data-sku]');
      if (skuEl && skuEl.getAttribute('data-sku').trim().toLowerCase() === term) link = a;
    });
    return link;
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showRecentSearches();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const matches = currentButtonText.match(new RegExp(previousTerm, 'g'));
      if (matches && matches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    // Angefangene Satzzeichen («101-0106-1001-10.») liefern bei Shopify 0 Treffer — abstreifen
    searchTerm = searchTerm.replace(/[.\-\s]+$/, '');
    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // B2B-optimiert: Produkte + Kollektionen + Queries, SKU-Suche aktiviert
    const params = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[type]': 'product,collection,query',
      'resources[limit]': 8,
      'resources[options][fields]': 'title,tag,variants.sku,variants.title',
      'resources[options][unavailable_products]': 'last',
    });

    fetch(`${routes.predictive_search_url}?${params.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        // Graceful fail: locale-aware Fallback-Link statt stilles Leerlaufen
        const q = encodeURIComponent(this.searchTerm || '');
        const isFR = window.location.pathname.startsWith('/fr');
        const searchUrl = isFR ? `/fr/search?q=${q}` : `/search?q=${q}`;
        this.predictiveSearchResults.innerHTML = `
          <div class="airtec-ps__fallback">
            <p>Suche momentan nicht verfügbar.</p>
            <a href="${searchUrl}" class="airtec-ps__fallback-link">Alle Ergebnisse anzeigen &rarr;</a>
          </div>`;
        this.setAttribute('results', true);
        this.open();
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.highlightMatchingSku();
    this.prioritizeSkuMatches();
    this.open();
  }

  // Treffer mit passender Bestell-Nr. zuoberst listen (Reihenfolge untereinander bleibt)
  prioritizeSkuMatches() {
    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;
    const matched = Array.from(list.children).filter((li) => li.querySelector('.airtec-ps__sku--matched'));
    matched.reverse().forEach((li) => list.prepend(li));
  }

  highlightMatchingSku() {
    if (!this.searchTerm) return;
    const term = this.searchTerm.toLowerCase();
    this.querySelectorAll('.airtec-ps__product-sku[data-sku]').forEach((el) => {
      const sku = el.getAttribute('data-sku').toLowerCase();
      if (sku.includes(term)) {
        el.classList.add('airtec-ps__sku--matched');
      } else {
        el.classList.remove('airtec-ps__sku--matched');
      }
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
